import React from 'react'
import cloneDeep from 'lodash/cloneDeep'
import isUndefined from 'lodash/isUndefined'
import isBoolean from 'lodash/isBoolean'
import isEqual from 'lodash/isEqual'

import {isEmpty} from 'utils/objectUtils'
import {run} from 'utils/validation/ruleRunner'
import withHooks from './FormHooks'

class FormContainer extends React.Component {
  constructor(props) {
    super(props)

    this.state = this.getState()
  }

  static getDerivedStateFromProps(props, currentState) {
    if (props.partialDataLoadRequired && !currentState.allDataLoaded && props.allDataLoaded) {
      return {
        data: {
          ...cloneDeep(props.data),
        },
        allDataLoaded: true,
        loading: false,
      }
    }
    return null
  }

  componentDidMount() {
    this._isMounted = true
  }

  componentDidUpdate(prevProps) {
    if (this.props.validations?.length !== prevProps.validations?.length) {
      this.setState(state => {
        const errors = run(state.data, this.props.validations)
        return {
          ...state,
          isValid: isEmpty(errors) && isEmpty(state.customErrors),
        }
      })
    }
  }

  componentWillUnmount() {
    this._isMounted = false
  }

  getState = () => ({
    data: {
      ...cloneDeep(this.props.data),
    },
    errors: {},
    customErrors: {},
    touched: {},
    loading: (this.props.partialDataLoadRequired || false) && !this.props.allDataLoaded,
    dirty: false,
    allDataLoaded: this.props.allDataLoaded,
    isValid: false,
  })

  onChange = (event, shouldResetCustomErrors = true) => {
    const {name, value} = event.target

    this.setState(state => {
      const newState = {
        ...state,
        data: {...state.data, [name]: value},
      }

      if (shouldResetCustomErrors) {
        this.props.resetCustomErrors && this.props.resetCustomErrors()
      }

      newState.errors = run(newState.data, this.props.validations)
      newState.isValid = isEmpty(newState.errors) && isEmpty(state.customErrors)
      return newState
    })
  }

  resetField = (name, {shouldUseInitialValue = true} = {}) => {
    this.setState(state => {
      const newState = {
        ...state,
      }

      if (shouldUseInitialValue && this.props.data[name]) {
        newState.data[name] = this.props.data[name]
      } else {
        delete newState.data[name]
      }

      delete newState.errors[name]
      delete newState.customErrors[name]
      return newState
    })
  }

  onBlur = event => {
    if (event && event.target) {
      const {name} = event.target

      this.setState(prevState => ({
        ...prevState,
        touched: {...prevState.touched, [name]: true},
      }))
    }
  }

  toggleLoadingState = loading => this._isMounted && this.setState(state => ({...state, loading}))

  modifyCustomError = (field, error) => {
    if (this._isMounted) {
      this.setState(state => {
        if (isUndefined(error)) {
          const {[field]: valueToRemove, ...customErrors} = state.customErrors
          return {
            ...state,
            customErrors: {
              ...customErrors,
            },
          }
        }

        return {
          ...state,
          customErrors: {
            ...state.customErrors,
            [field]: error,
          },
        }
      })
    }
  }

  isFormValid = () => {
    const errors = {
      ...run(this.state.data, this.props.validations),
    }

    const touched = {}

    Object.keys({...errors, ...this.state.customErrors}).forEach(key => {
      touched[key] = true
    })

    this.setState(state => ({
      ...state,
      errors,
      touched,
    }))

    return isEmpty(errors) && isEmpty(this.state.customErrors) && this.props.isExtraValid
  }

  scrollToError = () => {
    if (!isEmpty(this.getErrors())) {
      const errors = document.getElementsByClassName('error')
      if (errors.length) {
        const topError = errors.item(0)
        const rect = topError.getBoundingClientRect()
        if (rect.bottom > window.innerHeight) {
          topError.scrollIntoView(false)
        }
        if (rect.top < 0) {
          topError.scrollIntoView()
        }
      }
    }
  }

  canBeSubmitted = (statusArray = []) => {
    // submit hook can return undefined
    statusArray = statusArray.filter(isBoolean)

    // do not submit if false status exists
    const preventSubmit = statusArray.includes(false)

    /* There are nested forms where validation works from callSubmitHooks
       but for the outer form we should show validation too */
    return !this.state.loading && this.isFormValid() && !preventSubmit
  }

  onSubmit = async (e, msg) => {
    e && e.preventDefault()

    const statusArray = await this.props.callSubmitHooks()

    if (this.canBeSubmitted(statusArray)) {
      this.toggleLoadingState(true)
      try {
        await this.props.onSubmit?.(this.state.data)
        await this.props.onSuccess?.(msg)
        await this.props.callSuccessHooks()

        this.markFormAsClean()
      } catch (e) {
        this.props.onError && this.props.onError(e)
      } finally {
        this.toggleLoadingState(false)
      }
    } else {
      this.scrollToError()
    }
  }

  cancel = async e => {
    e.preventDefault()

    await this.props.callCancelHooks()

    this._isMounted && this.setState(this.getState())
  }

  /* Dirty pattern was initially implemented for energy address to display cancel button
     on edit link click. Then it was applied to customer number for the same purpose */
  markFormAsDirty = () => this.setState({dirty: true})

  /* This method is used when cancel button is clicked. Originally there was no intense to pass
     it to form children because multiple components might mark form dirty and there should be
     only one source of truth (cancel button) who calls clean method.
     Now it is also used for reset link for customer number rate limit so be aware there might be
     new issues if multiple dirty-allowed components are displayed on the same page. */
  markFormAsClean = () => this._isMounted && this.setState({dirty: false})

  isFormChanged = () => this.state.dirty || !isEqual(this.props.data, this.state.data)

  getErrors = () => ({...this.state.errors, ...this.state.customErrors, ...this.props.customErrors})

  render() {
    const {data, touched, loading, isValid} = this.state
    const initialData = this.props.data

    if (this.props.customErrors) {
      Object.keys(this.props.customErrors).forEach(key => {
        touched[key] = true
      })
    }

    return React.cloneElement(this.props.children, {
      data,
      initialData,
      errors: this.getErrors(),
      touched,
      changed: this.isFormChanged(),
      loading,
      markFormAsDirty: this.markFormAsDirty,
      markFormAsClean: this.markFormAsClean,
      onChange: this.onChange,
      onBlur: this.onBlur,
      onSubmit: this.onSubmit,
      cancel: this.cancel,
      toggleLoadingState: this.toggleLoadingState,
      modifyCustomError: this.modifyCustomError,
      addSubmitHook: this.props.addSubmitHook,
      addCancelHook: this.props.addCancelHook,
      addSuccessHook: this.props.addSuccessHook,
      resetField: this.resetField,
      isValid: isValid && this.props.isExtraValid,
    })
  }
}

FormContainer.defaultProps = {
  isExtraValid: true,
}

export {FormContainer as PureFormContainer}

export default withHooks(FormContainer)
